import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShoppingCart = ({ cart, selectedMember, onUpdateQuantity, onRemoveItem, onClearCart, onSelectMember, onCheckout }) => {
  const totals = useMemo(() => {
    const subtotal = cart?.reduce((sum, item) => {
      const price = parseFloat(item?.product?.base_price);
      return sum + (price * item?.quantity);
    }, 0);

    const tax = cart?.reduce((sum, item) => {
      const price = parseFloat(item?.product?.base_price);
      const taxRate = parseFloat(item?.product?.tax_rate || 0) / 100;
      return sum + (price * item?.quantity * taxRate);
    }, 0);

    const total = subtotal + tax;

    return { subtotal, tax, total };
  }, [cart]);

  return (
    <div className="bg-card rounded-lg border border-border sticky top-6">
      {/* Cart Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-foreground">Shopping Cart</h3>
          {cart?.length > 0 && (
            <Button variant="ghost" size="sm" onClick={onClearCart}>
              <Icon name="Trash2" size={16} className="mr-1" />
              Clear
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {cart?.length} {cart?.length === 1 ? 'item' : 'items'}
        </p>
      </div>

      {/* Cart Items */}
      <div className="p-4 max-h-[400px] overflow-y-auto">
        {cart?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="ShoppingCart" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Cart is empty</p>
          </div>
        ) : (
          <div className="space-y-3">
            {cart?.map((item) => (
              <div
                key={item?.product?.id}
                className="flex items-start gap-3 p-3 bg-background rounded-lg border border-border"
              >
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground text-sm truncate">
                    {item?.product?.name}
                  </h4>
                  <p className="text-xs text-muted-foreground mb-2">
                    ${item?.product?.base_price?.toFixed(2)} each
                  </p>
                  
                  {/* Quantity Controls */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onUpdateQuantity?.(item?.product?.id, item?.quantity - 1)}
                      className="w-7 h-7 flex items-center justify-center rounded bg-muted hover:bg-muted/80 text-foreground"
                    >
                      <Icon name="Minus" size={14} />
                    </button>
                    <span className="text-sm font-medium text-foreground w-8 text-center">
                      {item?.quantity}
                    </span>
                    <button
                      onClick={() => onUpdateQuantity?.(item?.product?.id, item?.quantity + 1)}
                      className="w-7 h-7 flex items-center justify-center rounded bg-muted hover:bg-muted/80 text-foreground"
                    >
                      <Icon name="Plus" size={14} />
                    </button>
                  </div>
                </div>

                <div className="text-right">
                  <p className="font-semibold text-foreground">
                    ${(item?.product?.base_price * item?.quantity)?.toFixed(2)}
                  </p>
                  <button
                    onClick={() => onRemoveItem?.(item?.product?.id)}
                    className="text-danger hover:bg-danger/10 p-1 rounded mt-1"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cart Summary */}
      {cart?.length > 0 && (
        <div className="p-4 border-t border-border">
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="text-foreground font-medium">${totals?.subtotal?.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Tax</span>
              <span className="text-foreground font-medium">${totals?.tax?.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-lg font-bold pt-2 border-t border-border">
              <span className="text-foreground">Total</span>
              <span className="text-primary">${totals?.total?.toFixed(2)}</span>
            </div>
          </div>

          <Button onClick={onCheckout} className="w-full">
            <Icon name="CreditCard" size={18} className="mr-2" />
            Checkout
          </Button>
        </div>
      )}
    </div>
  );
};

export default ShoppingCart;